import { Autocomplete, Box, Button, Card, IconButton, Paper, TextField } from "@mui/material"
import { ChangeEvent, Dispatch, Fragment, SetStateAction, SyntheticEvent, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useAppSelector } from "../../../../redux/store"
import { selectSubscriptionItems, selectTimeBasedTbrItems } from "../../../../redux/subscription"
import BioAlert from "./BioAlert"
import BioboxTable from "./BioboxTable"
import ToggleActiveButtons from "../../../../components/ToggleActiveButtons"
import { Biobox, ComplianceStatus } from "../../../../types"
import { BioboxFilters, StatusFilters, StatusFilterOption, OpenAssignBoxModal } from "../types"
import { getBackgroundComplianceColor } from "../../../../helpers/compliance"
import { getAutoShipmentStatus, getBioboxesByStatus, getBioboxExport, getComplianceStatuses, getFilteredBioboxes, getFormattedBioboxes, getHasUnassignedBoxes, getStatusFilterOptions } from "../utils/helpers"
import debounce from "../../../../helpers/debounce"
import useIsCorpView from "../../../../hooks/useIsCorpView"

type Bioboxes = {
  bioboxes: Biobox[],
  setBioboxes: Dispatch<SetStateAction<Biobox[]>>,
  setTabIndex: Dispatch<SetStateAction<number>>,
  setInventoryCount: Dispatch<SetStateAction<number>>,
  setOpenAssignBioboxModal: Dispatch<SetStateAction<OpenAssignBoxModal>>,
  customerNumber: string,
}

const Bioboxes = (props: Bioboxes) => {
  const {
    bioboxes,
    setBioboxes,
    setInventoryCount,
    setOpenAssignBioboxModal,
    customerNumber,
  } = props

  const navigate = useNavigate()

  const [search, setSearch] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [statusFilterOption, setStatusFilterOption] = useState<StatusFilterOption>({ label: StatusFilters.Active, status: BioboxFilters.Active })

  const subscriptions = useAppSelector(selectSubscriptionItems)
  const tbrEligibleItems = useAppSelector(selectTimeBasedTbrItems)
  const isCorpPracticeView = useIsCorpView()

  const statusFilterOptions: StatusFilterOption[] = useMemo(() => {
    return getStatusFilterOptions()
  }, [Object.keys(StatusFilters)])

  const formattedBioboxes = useMemo(() => {
    return getFormattedBioboxes(bioboxes)
  }, [bioboxes])

  const hasUnassignedBoxes = useMemo(() => {
    return getHasUnassignedBoxes(bioboxes)
  }, [bioboxes])

  const complianceStatuses = useMemo(() => {
    return getComplianceStatuses(formattedBioboxes)
  }, [formattedBioboxes])

  const bioboxesByStatus = useMemo(() => {
    return getBioboxesByStatus(formattedBioboxes)
  }, [formattedBioboxes])

  const filteredBioboxes = useMemo(() => {
    const status = isActive ? statusFilterOption.status : BioboxFilters.Inactive
    return getFilteredBioboxes(search, status, bioboxesByStatus)
  }, [isActive, statusFilterOption, search, bioboxesByStatus])

  const { hasAutoShipment, hasEligibleItems } = useMemo(() => {
    return getAutoShipmentStatus(bioboxes, tbrEligibleItems, subscriptions)
  }, [subscriptions, tbrEligibleItems])

  const handleSearch = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const searchValue = event.target.value
    setSearch(searchValue)
  }

  const handleStatusFilter = (event: SyntheticEvent<Element, Event>, value: StatusFilterOption | null) => {
    if (value) setStatusFilterOption(value)
  }

  const handleExport = () => {
    getBioboxExport(filteredBioboxes)
  }

  const handleAutoShipments = () => {
    const link = isCorpPracticeView
      ? `/corp/practice/${customerNumber}/auto-renewals`
      : '/practice/auto-renewals'

    navigate(link)
  }

  const debouncedHandleSearch = debounce(handleSearch, 50)

  useEffect(() => {
    setInventoryCount(filteredBioboxes.length)
  }, [filteredBioboxes])

  return (
    <>
      <Box sx={{ p: 1.5 }}>
        <Box className="flex flex-center m-b-16">
          {hasUnassignedBoxes && <BioAlert />}
        </Box>
        <Box className="flex flex-end">
          {
            hasEligibleItems && (
              <Button variant="contained" onClick={handleAutoShipments} sx={{ margin: '16px 0px' }}>
                {hasAutoShipment ? 'Edit Auto Shipments' : 'Enroll in Auto Shipments'}
              </Button>
            )
          }
        </Box>
        <Card sx={{ width: '100%' }} variant="outlined">
          <Box className="box-header flex flex-space-between">
            <span>Spore Test Boxes</span>
            <Box>
              {
                [
                  ComplianceStatus.Expired,
                  ComplianceStatus.Expiring,
                  ComplianceStatus.Ok,
                  ComplianceStatus.Inactive,
                ].map(status => {
                  return (
                    <Fragment key={`status-${status}`}>
                      <span className={`m-r-8 compliance-circle ${getBackgroundComplianceColor(status)}`} />
                      <span className="m-r-12 muted-text">{complianceStatuses[status]}</span>
                    </Fragment>
                  )
                })
              }
            </Box>
          </Box>
          <Box className="table-subheader flex flex-end">
            <Button variant="contained" onClick={handleExport}>Export</Button>
          </Box>
          <Box className="table-subheader flex flex-space-between" sx={{ paddingTop: '16px' }}>
            <Box sx={{ columnGap: '16px' }} className="flex">
              <TextField
                sx={{ minWidth: 200, '& .MuiInputBase-root': { paddingRight: '4px' } }}
                size="small"
                label="Search"
                onChange={debouncedHandleSearch}
                InputProps={{
                  endAdornment: (
                    <IconButton>
                      <FontAwesomeIcon icon="magnifying-glass" size="sm" className="m-l-8" />
                    </IconButton>
                  )
                }}
              />
              {
                isActive && (
                  <Autocomplete
                    value={statusFilterOption}
                    onChange={handleStatusFilter}
                    options={statusFilterOptions}
                    isOptionEqualToValue={(option: StatusFilterOption, value: StatusFilterOption) => (
                      option.status === value.status
                    )}
                    getOptionLabel={option => String(option.label)}
                    size="small"
                    style={{ minWidth: 250 }}
                    renderInput={(params) => (
                      <TextField {...params} label="Status" />
                    )}
                  />
                )
              }
            </Box>
            <ToggleActiveButtons isActive={isActive} setIsActive={setIsActive} />
          </Box>
          <Box sx={{ padding: '16px' }}>
            <Paper>
              <BioboxTable
                isActive={isActive}
                bioboxes={filteredBioboxes}
                setBioboxes={setBioboxes}
                setOpenAssignBioboxModal={setOpenAssignBioboxModal}
              />
            </Paper>
          </Box>
        </Card>
      </Box>
    </>
  )
}

export default Bioboxes